import React, { useCallback, useEffect, useRef, useState } from "react";

import { mdiChevronRight } from "@mdi/js";
import Icon from "@mdi/react";

import MenuItem from "./MenuItem";
import { useSidebarContext } from "./Sidebar";
import SubMenuPortal from "./SubMenuPortal";
import AbilityContainer from "../utils/abilities/AbilityContainer";

import type { JSX, ReactNode } from "react";

import type { AbilityKey } from "../utils/abilities/AbilityContainer";

interface SubMenuProps {
  children: ReactNode;
  label: string;
  isDefaultOpen?: boolean;
  icon?: JSX.Element;
  abilityList?: AbilityKey[] | undefined;
}

export default function SubMenu({
  label,
  icon,
  isDefaultOpen = false,
  abilityList,
  children,
}: SubMenuProps): JSX.Element {
  const [isOpen, setIsOpen] = useState(isDefaultOpen);
  const menuItemRef = useRef<HTMLDivElement>(null);
  const submenuRef = useRef<HTMLUListElement>(null);
  const { isCollapsed } = useSidebarContext();

  const handleToggle = (): void => {
    setIsOpen((currentIsOpen) => !currentIsOpen);
  };

  useEffect(() => {
    if (isCollapsed) return;

    if (isOpen) {
      submenuRef.current?.style.setProperty(
        "height",
        `${submenuRef.current?.scrollHeight}px`,
      );
    } else {
      submenuRef.current?.style.setProperty("height", "0px");
    }
  }, [isOpen, submenuRef, isCollapsed, children]);

  useEffect(() => {
    if (isCollapsed) {
      setIsOpen(false);
    }
  }, [isCollapsed]);

  const handleClose = useCallback(
    (event: MouseEvent) => {
      if (isCollapsed && isOpen) {
        if (
          event.target instanceof Node &&
          menuItemRef.current?.contains(event.target)
        ) {
          return;
        }

        setIsOpen(false);
      }
    },
    [isOpen, setIsOpen, isCollapsed],
  );

  useEffect(() => {
    document.addEventListener("click", handleClose);

    return () => {
      document.removeEventListener("click", handleClose);
    };
  }, [handleClose]);

  return (
    <AbilityContainer abilityList={abilityList}>
      <MenuItem
        ref={menuItemRef}
        className="cursor-pointer uppercase"
        component={<div onClick={handleToggle} />}
        icon={icon}
        isSubmenuOpen={isOpen}
        label={label}
        suffix={
          <Icon
            className="text-muted-foreground transition-all"
            path={mdiChevronRight}
            rotate={isOpen ? 90 : 0}
            size={0.96875}
          />
        }
      />
      {!isCollapsed && (
        <ul
          ref={submenuRef}
          className="bg-muted overflow-hidden transition-all duration-300 ease-in-out [&>*>div]:pl-12.5"
        >
          {children}
        </ul>
      )}
      <SubMenuPortal currentMenuRef={menuItemRef} isOpen={isOpen}>
        {children}
      </SubMenuPortal>
    </AbilityContainer>
  );
}
